import { getApiBaseUrl } from '../config/env';
import { mapPublicPrivacyPayload, hasPrivacyContent } from './mapPublicPrivacy';

const CACHE_KEY = 'jobscribe_public_privacy_v1';
const CACHE_TTL_MS = 10 * 60 * 1000;

/** @type {Promise<ReturnType<typeof mapPublicPrivacyPayload>> | null} */
let inflightRequest = null;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return mapPublicPrivacyPayload(parsed);
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        fetchedAt: Date.now(),
        hero: payload.hero,
        sections: payload.sections,
        cta: payload.cta,
      })
    );
  } catch {
    // ignore
  }
}

export function getCachedPublicPrivacy() {
  return readCache();
}

/**
 * @param {{ forceRefresh?: boolean }} [options]
 */
export async function fetchPublicPrivacy(options = {}) {
  const { forceRefresh = false } = options;

  if (!forceRefresh) {
    const cached = readCache();
    if (cached && hasPrivacyContent(cached)) return cached;
  }

  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    const res = await fetch(`${getApiBaseUrl()}/public/privacy`, {
      credentials: 'omit',
    });
    if (!res.ok) {
      throw new Error('Failed to fetch privacy policy');
    }
    const json = await res.json();
    const data = json.data ?? json;
    const payload = mapPublicPrivacyPayload(data);
    if (hasPrivacyContent(payload)) {
      writeCache(payload);
    }
    return payload;
  })();

  try {
    return await inflightRequest;
  } finally {
    inflightRequest = null;
  }
}

export function prefetchPublicPrivacy() {
  const cached = readCache();
  if (cached && hasPrivacyContent(cached)) return;
  void fetchPublicPrivacy().catch(() => {});
}
